import React, { useState, useRef, useEffect } from 'react';
import { X, Settings, Users, Shield, Trash2, Sun, Moon, Monitor, Sparkles, Check, Palette, Globe, Upload, Loader2, type LucideIcon } from 'lucide-react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useAuth } from '../../context/AuthContext';
import { useTheme, type ThemeMode } from '../../hooks/useTheme';
import {
  useLanguage,
  SUPPORTED_LANGUAGES,
  TRANSLATION_TARGET_LANGUAGES,
  type SupportedLanguage,
} from '../../hooks/useLanguage';
import { api } from '../../lib/api';
import { ThemeStudio } from '../theme/ThemeStudio';
import { UserAvatar } from '../common/UserAvatar';
import { WorkspaceLogo } from '../common/WorkspaceLogo';
import { ConfirmDeleteModal } from './ConfirmDeleteModal';
import { ImageCropperModal } from './ImageCropperModal';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'general' | 'appearance' | 'language' | 'members';
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ 
  isOpen, 
  onClose,
  initialTab = 'general'
}) => {
  const { currentWorkspace, members, refreshMembers, updateWorkspace, uploadWorkspaceIcon, removeWorkspaceIcon, deleteWorkspace } = useWorkspace();
  const { user } = useAuth();
  const { theme, setTheme, resolvedTheme } = useTheme();
  const {
    language,
    setLanguage,
    preferredTranslationLanguage,
    setPreferredTranslationLanguage,
    autoTranslate,
    setAutoTranslate,
    t,
  } = useLanguage();

  const fileInputRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<'general' | 'appearance' | 'language' | 'members'>(initialTab);
  const [name, setName] = useState(currentWorkspace?.name || '');
  const [description, setDescription] = useState(currentWorkspace?.description || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isUploadingIcon, setIsUploadingIcon] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  // Icon Cropper State
  const [selectedCropFile, setSelectedCropFile] = useState<File | null>(null);
  const [isCropperOpen, setIsCropperOpen] = useState<boolean>(false);

  useEffect(() => {
    if (currentWorkspace) {
      setName(currentWorkspace.name);
      setDescription(currentWorkspace.description || '');
    }
  }, [currentWorkspace]);

  if (!isOpen || !currentWorkspace) return null;

  const currentMember = members.find((m) => m.user.id === user?.id);
  const isAdminOrOwner =
    user?.globalRole === 'SUPER_ADMIN' ||
    currentMember?.role === 'ADMIN' ||
    currentMember?.role === 'OWNER' ||
    members.length === 0;

  const handleSaveGeneral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    try {
      setIsSaving(true);
      setError(null);
      await updateWorkspace(name.trim(), description.trim() || undefined);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to update workspace');
    } finally {
      setIsSaving(false);
    }
  };

  const handleIconChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setError('Please select a valid image file (PNG, JPG, WebP, GIF, SVG)');
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      setError('Image size must be less than 10MB');
      return;
    }

    setError(null);
    setSelectedCropFile(file);
    setIsCropperOpen(true);
  };

  const handleCropComplete = async (croppedFile: File) => {
    try {
      setIsUploadingIcon(true);
      setError(null);
      await uploadWorkspaceIcon(croppedFile);
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to upload workspace icon');
    } finally {
      setIsUploadingIcon(false);
      setSelectedCropFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveIcon = async () => {
    try {
      setIsUploadingIcon(true);
      setError(null);
      await removeWorkspaceIcon();
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 2000);
    } catch (err: any) {
      const serverMsg = err?.response?.data?.error?.message || err?.response?.data?.message || err?.message;
      setError(serverMsg || 'Failed to remove workspace icon');
    } finally {
      setIsUploadingIcon(false);
    }
  };

  const handleRemoveMember = async (targetUserId: string) => {
    if (!confirm(t('settings.removeMemberConfirm'))) return;
    try {
      await api.workspaces.removeMember(currentWorkspace.id, targetUserId);
      await refreshMembers();
    } catch (err) {
      console.error('Failed to remove member', err);
    }
  };

  const THEME_OPTIONS: Array<{
    id: ThemeMode;
    label: string;
    description: string;
    icon: LucideIcon;
    previewBg: string;
    previewSidebar: string;
    previewCard: string;
    previewText: string;
  }> = [
    {
      id: 'light',
      label: t('settings.themeLight'),
      description: t('settings.themeLightDesc'),
      icon: Sun,
      previewBg: 'bg-[#FAFAFC]',
      previewSidebar: 'bg-[#F3F2F8]',
      previewCard: 'bg-[#FFFFFF] border-slate-200',
      previewText: 'text-slate-900',
    },
    {
      id: 'dark',
      label: t('settings.themeDark'),
      description: t('settings.themeDarkDesc'),
      icon: Moon,
      previewBg: 'bg-[#0C0F17]',
      previewSidebar: 'bg-[#111522]',
      previewCard: 'bg-[#181E2E] border-slate-700/60',
      previewText: 'text-slate-100',
    },
    {
      id: 'system',
      label: t('settings.themeSystem'),
      description: t('settings.themeSystemDesc'),
      icon: Monitor,
      previewBg: resolvedTheme === 'dark' ? 'bg-[#0C0F17]' : 'bg-[#FAFAFC]',
      previewSidebar: 'bg-[#1a1b4b]',
      previewCard: resolvedTheme === 'dark' ? 'bg-[#181E2E] border-slate-700/60' : 'bg-[#FFFFFF] border-slate-200',
      previewText: resolvedTheme === 'dark' ? 'text-slate-100' : 'text-slate-900',
    },
    {
      id: 'custom',
      label: 'Custom Theme',
      description: 'Fully customize colors, sidebars, chat, buttons & overlays',
      icon: Sparkles,
      previewBg: 'bg-gradient-to-br from-indigo-950/90 via-slate-900 to-purple-950/90',
      previewSidebar: 'bg-[#1a1b4b]',
      previewCard: 'bg-slate-900/90 border-indigo-500/40',
      previewText: 'text-indigo-200',
    },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4">
      <div className={`w-full ${tab === 'appearance' ? 'max-w-5xl' : 'max-w-2xl'} bg-card border border-border rounded-3xl shadow-2xl overflow-hidden flex flex-col h-[88vh] max-h-[850px] animate-in fade-in zoom-in-95 duration-200 transition-all`}>
        {/* Header - Always pinned at top */}
        <div className="p-5 border-b border-border flex items-center justify-between flex-shrink-0 bg-card z-20">
          <div className="flex items-center gap-2.5 font-bold text-base text-foreground">
            <div className="w-8 h-8 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Settings className="h-4 w-4" />
            </div>
            <span>{t('settings.title')}</span>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-xl hover:bg-muted text-muted-foreground hover:text-foreground transition"
            aria-label={t('common.close')}
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Tabs - Always pinned under header with flex-shrink-0 and solid background */}
        <div className="flex border-b border-border px-5 gap-1.5 text-xs font-semibold overflow-x-auto no-scrollbar flex-shrink-0 bg-card z-20">
          <button
            type="button"
            onClick={() => setTab('general')}
            className={`py-3 px-3.5 border-b-2 transition flex items-center gap-2 whitespace-nowrap cursor-pointer rounded-t-lg ${
              tab === 'general'
                ? 'border-primary text-primary font-bold bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <Settings className="h-4 w-4" />
            <span>{t('settings.general')}</span>
          </button>

          <button
            type="button"
            onClick={() => setTab('appearance')}
            className={`py-3 px-3.5 border-b-2 transition flex items-center gap-2 whitespace-nowrap cursor-pointer rounded-t-lg ${
              tab === 'appearance'
                ? 'border-primary text-primary font-bold bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <Palette className="h-4 w-4" />
            <span>{t('settings.appearance')}</span>
          </button>

          <button
            type="button"
            onClick={() => setTab('language')}
            className={`py-3 px-3.5 border-b-2 transition flex items-center gap-2 whitespace-nowrap cursor-pointer rounded-t-lg ${
              tab === 'language'
                ? 'border-primary text-primary font-bold bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <Globe className="h-4 w-4" />
            <span>{t('settings.language')}</span>
          </button>

          <button
            type="button"
            onClick={() => setTab('members')}
            className={`py-3 px-3.5 border-b-2 transition flex items-center gap-2 whitespace-nowrap cursor-pointer rounded-t-lg ${
              tab === 'members'
                ? 'border-primary text-primary font-bold bg-primary/5'
                : 'border-transparent text-muted-foreground hover:text-foreground hover:bg-muted/40'
            }`}
          >
            <Users className="h-4 w-4" />
            <span>{t('settings.members')} ({members.length})</span>
          </button>
        </div>

        {/* Tab Content - Dedicated scroll container with min-h-0 */}
        <div className="p-6 overflow-y-auto flex-1 min-h-0 bg-card">
          {tab === 'general' && (
            <form onSubmit={handleSaveGeneral} className="space-y-5">
              {error && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-xs font-medium rounded-xl">
                  {error}
                </div>
              )}

              {saveSuccess && (
                <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-medium rounded-xl flex items-center gap-1.5">
                  <Check className="h-3.5 w-3.5" />
                  <span>{t('settings.savedSuccess')}</span>
                </div>
              )}

              {/* Workspace Icon Management */}
              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">Workspace Icon</label>
                <div className="flex items-center gap-4 p-3.5 rounded-2xl bg-muted/30 border border-border">
                  <div className="relative group">
                    <WorkspaceLogo
                      workspaceId={currentWorkspace.id}
                      name={currentWorkspace.name}
                      avatarUrl={currentWorkspace.avatarUrl}
                      size="lg"
                      className="ring-2 ring-border shadow-xs"
                    />
                    {isUploadingIcon && (
                      <div className="absolute inset-0 rounded-2xl bg-black/50 backdrop-blur-2xs flex items-center justify-center text-white">
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </div>
                    )}
                  </div>

                  <div className="flex-1 min-w-0">
                    {isAdminOrOwner ? (
                      <>
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleIconChange}
                          accept="image/png,image/jpeg,image/webp,image/gif"
                          className="hidden"
                        />
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            disabled={isUploadingIcon}
                            onClick={() => fileInputRef.current?.click()}
                            className="px-3 py-1.5 bg-background hover:bg-muted text-foreground border border-border rounded-xl text-xs font-semibold inline-flex items-center gap-1.5 transition shadow-2xs disabled:opacity-50"
                          >
                            <Upload className="h-3.5 w-3.5 text-primary" />
                            <span>{currentWorkspace.avatarUrl ? 'Change Icon' : 'Upload Icon'}</span>
                          </button>

                          {currentWorkspace.avatarUrl && (
                            <button
                              type="button"
                              disabled={isUploadingIcon}
                              onClick={handleRemoveIcon}
                              className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 border border-border rounded-xl transition disabled:opacity-50"
                              title="Remove Icon"
                              aria-label="Remove Icon"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </div>
                        <p className="text-[11px] text-muted-foreground mt-1">PNG, JPG, WebP, SVG up to 5MB</p>
                      </>
                    ) : (
                      <p className="text-xs text-muted-foreground">Only workspace Admins can modify the workspace icon.</p>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">{t('settings.workspaceName')}</label>
                <input
                  type="text"
                  required
                  disabled={!isAdminOrOwner}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full h-10 px-3.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition text-foreground disabled:opacity-70 disabled:bg-muted"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">{t('settings.urlSlug')}</label>
                <input
                  type="text"
                  readOnly
                  disabled
                  value={currentWorkspace.slug}
                  className="w-full h-10 px-3.5 bg-muted border border-border rounded-xl text-xs font-mono opacity-70 text-muted-foreground"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-foreground mb-1.5">{t('settings.workspaceDescription')}</label>
                <textarea
                  rows={3}
                  disabled={!isAdminOrOwner}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="e.g. Engineering & Design organization workspace"
                  className="w-full p-3.5 bg-background border border-border rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary transition resize-none text-foreground disabled:opacity-70 disabled:bg-muted"
                />
              </div>

              {isAdminOrOwner && (
                <div className="pt-2 flex justify-end">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="px-5 py-2.5 bg-primary text-primary-foreground text-xs font-semibold rounded-xl hover:bg-primary/90 transition shadow-sm disabled:opacity-50 cursor-pointer"
                  >
                    {isSaving ? t('common.saving') : t('settings.saveChanges')}
                  </button>
                </div>
              )}

              {isAdminOrOwner && (
                <div className="pt-6 border-t border-destructive/20 space-y-3">
                  <h4 className="text-xs font-bold text-destructive uppercase tracking-wider">Danger Zone</h4>
                  <div className="p-4 rounded-2xl border border-destructive/20 bg-destructive/5 flex items-center justify-between gap-4">
                    <div>
                      <p className="text-xs font-bold text-foreground">Delete this workspace</p>
                      <p className="text-[11px] text-muted-foreground mt-0.5">
                        Once deleted, all channels, messages, meetings, and files will be permanently removed.
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setConfirmDeleteOpen(true)}
                      className="px-3.5 py-2 bg-destructive text-white text-xs font-semibold rounded-xl hover:bg-destructive/90 transition shadow-xs shrink-0 flex items-center gap-1.5 cursor-pointer"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      <span>Delete Workspace</span>
                    </button>
                  </div>
                </div>
              )}
            </form>
          )}

          {tab === 'appearance' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-bold text-foreground">{t('settings.appearance')}</h3>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Choose your base visual appearance mode or fine-tune individual design tokens.
                </p>
              </div>

              {/* Base Appearance Modes */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3.5">
                {THEME_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const isSelected = theme === opt.id;

                  return (
                    <button
                      key={opt.id}
                      type="button"
                      onClick={() => setTheme(opt.id)}
                      className={`relative flex flex-col p-3.5 rounded-2xl border text-left transition-all duration-200 group ${
                        isSelected
                          ? 'border-primary bg-primary/[0.04] ring-2 ring-primary/20 shadow-md scale-[1.01]'
                          : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                      }`}
                    >
                      {isSelected && (
                        <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-sm">
                          <Check className="h-3 w-3 stroke-[3]" />
                        </div>
                      )}

                      <div className={`w-full h-16 rounded-xl mb-2.5 overflow-hidden border flex ${opt.previewBg} p-1.5 gap-1.5 shadow-inner`}>
                        <div className={`w-1/3 h-full rounded-lg ${opt.previewSidebar} flex flex-col gap-1 p-1`}>
                          <div className="w-full h-1.5 rounded bg-primary/40" />
                          <div className="w-3/4 h-1 rounded bg-muted-foreground/30" />
                          <div className="w-1/2 h-1 rounded bg-muted-foreground/30" />
                        </div>
                        <div className="flex-1 h-full flex flex-col gap-1">
                          <div className={`w-full flex-1 rounded-lg border p-1 ${opt.previewCard} flex flex-col gap-1`}>
                            <div className="w-2/3 h-1.5 rounded bg-primary/50" />
                            <div className="w-full h-1 rounded bg-muted-foreground/25" />
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-1.5 mb-1">
                        <Icon className={`h-4 w-4 ${isSelected ? 'text-primary' : 'text-muted-foreground group-hover:text-foreground'}`} />
                        <span className={`text-xs font-bold ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                          {opt.label}
                        </span>
                      </div>

                      <p className="text-[11px] text-muted-foreground leading-relaxed line-clamp-2">
                        {opt.description}
                      </p>
                    </button>
                  );
                })}
              </div>

              {/* Status pill */}
              <div className="p-3 bg-muted/40 rounded-xl border border-border/80 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Active Appearance Mode</span>
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  {theme === 'custom'
                    ? 'Custom Theme (Studio Active)'
                    : theme === 'system'
                    ? `Auto (${resolvedTheme === 'dark' ? t('settings.themeDark') : t('settings.themeLight')})`
                    : theme === 'dark'
                    ? t('settings.themeDark')
                    : t('settings.themeLight')}
                </span>
              </div>

              {/* Dedicated Theme Customization Studio */}
              <div className="pt-4 border-t border-border space-y-4">
                <div>
                  <h3 className="text-base font-bold text-foreground flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-primary" />
                    <span>Theme Customization</span>
                  </h3>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
                    Completely customize the visual color system of Sahaj Connect. Select presets, inspect real-time live preview, check accessibility contrast, and fine-tune individual components.
                  </p>
                </div>

                <ThemeStudio />
              </div>
            </div>
          )}

          {tab === 'language' && (
            <div className="space-y-7">
              {/* 1. Application UI Language */}
              <div className="space-y-3">
                <div>
                  <h3 className="text-sm font-bold text-foreground">{t('settings.applicationLanguage')}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('settings.applicationLanguageDesc')}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {SUPPORTED_LANGUAGES.map((langItem) => {
                    const isSelected = language === langItem.code;

                    return (
                      <button
                        key={langItem.code}
                        type="button"
                        onClick={() => setLanguage(langItem.code as SupportedLanguage)}
                        className={`relative flex items-center justify-between p-3.5 rounded-2xl border text-left transition-all duration-200 group ${
                          isSelected
                            ? 'border-primary bg-primary/[0.05] ring-2 ring-primary/20 shadow-md scale-[1.01]'
                            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="text-2xl select-none" role="img" aria-label={langItem.name}>
                            {langItem.flag}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-bold text-foreground truncate">
                              {langItem.nativeName}
                            </p>
                            <p className="text-xs text-muted-foreground truncate">
                              {langItem.name} ({langItem.code.toUpperCase()})
                            </p>
                          </div>
                        </div>

                        <div
                          className={`h-5 w-5 rounded-full flex items-center justify-center border transition flex-shrink-0 ${
                            isSelected
                              ? 'bg-primary border-primary text-primary-foreground shadow-xs'
                              : 'border-border bg-muted/40'
                          }`}
                        >
                          {isSelected && <Check className="h-3.5 w-3.5 stroke-[3]" />}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 2. Message Translation Target Language */}
              <div className="space-y-3 pt-5 border-t border-border/70">
                <div>
                  <h3 className="text-sm font-bold text-foreground">{t('settings.messageTranslationLanguage')}</h3>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {t('settings.messageTranslationLanguageDesc')}
                  </p>
                </div>

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
                  {TRANSLATION_TARGET_LANGUAGES.map((targetLang) => {
                    const isSelected = preferredTranslationLanguage === targetLang.code;

                    return (
                      <button
                        key={targetLang.code}
                        type="button"
                        onClick={() => setPreferredTranslationLanguage(targetLang.code)}
                        className={`flex flex-col items-start p-3 rounded-xl border text-left transition-all duration-150 ${
                          isSelected
                            ? 'border-primary bg-primary/[0.08] ring-2 ring-primary/20 shadow-xs'
                            : 'border-border bg-card hover:border-primary/40 hover:bg-muted/30'
                        }`}
                      >
                        <div className="flex items-center justify-between w-full mb-1">
                          <span className="text-lg select-none">{targetLang.flag}</span>
                          <span className="text-[10px] font-mono font-bold px-1.5 py-0.5 rounded bg-muted/70 text-muted-foreground">
                            {targetLang.code.toUpperCase()}
                          </span>
                        </div>
                        <span className="text-xs font-bold text-foreground truncate w-full">
                          {targetLang.nativeName}
                        </span>
                        <span className="text-[11px] text-muted-foreground truncate w-full">
                          {targetLang.name}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* 3. Automatic Message Translation Toggle */}
              <div className="space-y-3 pt-5 border-t border-border/70">
                <div className="flex items-center justify-between p-4 rounded-2xl border border-border bg-card/60">
                  <div className="space-y-0.5 pr-4">
                    <h4 className="text-sm font-bold text-foreground">{t('settings.autoTranslate')}</h4>
                    <p className="text-xs text-muted-foreground leading-relaxed">
                      {t('settings.autoTranslateDesc')}
                    </p>
                  </div>

                  <button
                    type="button"
                    onClick={() => setAutoTranslate(!autoTranslate)}
                    aria-checked={autoTranslate}
                    role="switch"
                    className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 ${
                      autoTranslate ? 'bg-primary' : 'bg-muted'
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg ring-0 transition duration-200 ease-in-out ${
                        autoTranslate ? 'translate-x-5' : 'translate-x-0'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Status summary pill */}
              <div className="p-3 bg-muted/40 rounded-xl border border-border/80 flex items-center justify-between text-xs">
                <span className="text-muted-foreground">{t('settings.currentLanguage')}</span>
                <span className="font-semibold text-foreground flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  {SUPPORTED_LANGUAGES.find((l) => l.code === language)?.nativeName} ({language.toUpperCase()})
                  {' • '}
                  Target: {TRANSLATION_TARGET_LANGUAGES.find((l) => l.code === preferredTranslationLanguage)?.nativeName}
                </span>
              </div>
            </div>
          )}

          {tab === 'members' && (
            <div className="divide-y divide-border/60">
              {members.map((m) => {
                const isSelf = m.user.id === user?.id;

                return (
                  <div key={m.id} className="py-3 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <UserAvatar
                        userId={m.user.id}
                        displayName={m.user.displayName}
                        avatarUrl={m.user.avatarUrl}
                        size="sm"
                        className="shadow-2xs shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="font-semibold text-sm text-foreground truncate flex items-center gap-2">
                          <span>{m.user.displayName}</span>
                          {isSelf && <span className="text-[10px] bg-muted px-1.5 py-0.5 rounded text-muted-foreground">{t('common.you')}</span>}
                        </p>
                        <p className="text-xs text-muted-foreground truncate">{m.role}</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-primary/10 text-primary flex items-center gap-1">
                        <Shield className="h-3 w-3" />
                        <span>{m.role}</span>
                      </span>

                      {!isSelf && m.role !== 'OWNER' && (
                        <button
                          onClick={() => handleRemoveMember(m.user.id)}
                          className="p-1.5 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition"
                          title={t('settings.removeMember')}
                          aria-label={t('settings.removeMember')}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {confirmDeleteOpen && currentWorkspace && (
        <ConfirmDeleteModal
          isOpen={true}
          onClose={() => setConfirmDeleteOpen(false)}
          type="workspace"
          itemName={currentWorkspace.name}
          onConfirm={async () => {
            await deleteWorkspace(currentWorkspace.id);
            setConfirmDeleteOpen(false);
            onClose();
          }}
        />
      )}

      {/* Interactive Workspace Icon Cropper */}
      {isCropperOpen && selectedCropFile && (
        <ImageCropperModal
          isOpen={isCropperOpen}
          imageFile={selectedCropFile}
          cropShape="rounded-square"
          title="Crop Workspace Logo"
          onClose={() => {
            setIsCropperOpen(false);
            setSelectedCropFile(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = '';
            }
          }}
          onCropComplete={handleCropComplete}
        />
      )}
    </div>
  );
};
